import { format } from 'date-fns';
import type { AgencyHistoryChange, ProjectHistoryEvent } from '~/api/projectAgencyHistory';

// Presentation helpers for the admin agency-history popup. Kept out of the component so
// they're unit-testable — packages/client has no React renderer (no jsdom / testing-library).

export type ChangeTypeMeta = { symbol: string; color: 'green' | 'red' | 'yellow' };

export const CHANGE_TYPE_META: Record<AgencyHistoryChange['changeType'], ChangeTypeMeta> = {
  added: { symbol: '+', color: 'green' },
  removed: { symbol: '−', color: 'red' },
  modified: { symbol: '~', color: 'yellow' },
};

/**
 * The History trigger is an internal diagnostic: admin-only, no feature flag, and
 * pointless without a project to read history for.
 */
export function shouldShowAgencyHistory(inputs: { isAdmin: boolean | undefined; projectId: string | undefined }) {
  return !!inputs.isAdmin && !!inputs.projectId;
}

/**
 * Reasons are stored as free text server-side ('manualRecompute', 'agencyChange', …), so render
 * whatever arrives rather than mapping a closed set — a newly added reason still reads sensibly.
 */
export function formatReason(reason: string): string {
  const spaced = reason.replace(/([a-z0-9])([A-Z])/g, '$1 $2').trim();
  if (!spaced) return '';
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

export function formatEventTimestamp(createdAt: string): string {
  const date = new Date(createdAt);
  // A malformed timestamp shouldn't blank the whole popup — show the raw value instead.
  if (Number.isNaN(date.getTime())) return createdAt;
  return format(date, 'MMM d, yyyy h:mmaaa');
}

/** The `+5 −1 ~2` summary shown on a collapsed update row. */
export function formatEventSummary(counts: ProjectHistoryEvent['counts']): string {
  return `+${counts.added} −${counts.removed} ~${counts.modified}`;
}

/**
 * Name and type are snapshotted at capture time, so this normally always has both. The type is
 * shown because a city that runs several utilities appears once per utility under the same name —
 * `City of Fremont (water)` vs `City of Fremont (sewer)`. Both are null once the agency is
 * hard-deleted, so fall back to the bare name and then to the id.
 */
export function agencyLabel(change: Pick<AgencyHistoryChange, 'agencyId' | 'agencyName' | 'agencyType'>): string {
  if (!change.agencyName) return `(deleted agency ${change.agencyId})`;
  return change.agencyType ? `${change.agencyName} (${change.agencyType})` : change.agencyName;
}

/**
 * What a changed field now holds, for the "what was modified" detail lines. Only the post-write
 * value is captured, so this is the new value — not a before → after diff.
 *
 * An unrecognised field name still renders (as the bare field name): `changedFields` is written
 * server-side from whatever columns a write path touched, so a newly captured column should show
 * up here rather than vanish.
 */
export function formatChangedField(field: string, change: AgencyHistoryChange): string {
  switch (field) {
    case 'assetsInProjectArea':
      return `assets in project area: ${change.assetsInProjectArea ? 'yes' : 'no'}`;
    case 'buildingLocations': {
      const count = change.buildingLocationCount ?? 0;
      return `buildingLocations: ${count === 0 ? 'none' : `${count} building${count === 1 ? '' : 's'}`}`;
    }
    default:
      return field;
  }
}

/**
 * The detail lines for one change. Empty for 'removed' (changedFields is [] — the row is gone, so
 * there is no "what changed"), and for an 'added' change the captured-field list is the whole
 * initial state, which the row already summarises — so detail is only shown for 'modified'.
 */
export function changedFieldLines(change: AgencyHistoryChange): string[] {
  if (change.changeType !== 'modified') return [];
  return change.changedFields.map((field) => formatChangedField(field, change));
}
